import { reportPlanSummaryMap } from "@ishvaram/domain-data/report-plan-summaries";
import OfferImpression from "@/components/OfferImpression";
import BridgeOfferLink from "@/components/BridgeOfferLink";
import DepthPaywall from "@/components/DepthPaywall";
import {
  buildReportInputHref,
  resolvePageReportOffer,
  type ReportInputBirthContext,
} from "@/lib/page-report-offer";
import type { SEOIntent, SEOReportPlanId } from "@/lib/seo-intents";
import { offerPresentationId } from "@/lib/analytics-offer-milestones";

export default function CalculatorReportBridge({
  planId,
  source,
  intent,
  placement = `${source}_report_bridge`,
  title,
  body,
  cta = "Get chart-backed report",
  language = "en",
  birthContext,
}: {
  /** Omit to derive the plan from the governed source -> intent map (rule 40). */
  planId?: SEOReportPlanId;
  source: string;
  /** Omit with planId so impression and click use the page map's canonical intent. */
  intent?: SEOIntent;
  placement?: string;
  title: string;
  body: string;
  cta?: string;
  language?: "en" | "hi";
  birthContext?: ReportInputBirthContext;
}) {
  const governedOffer = resolvePageReportOffer(source);
  const resolvedPlanId = planId ?? governedOffer.planId;
  const resolvedIntent = intent ?? governedOffer.intent;
  const report = reportPlanSummaryMap[resolvedPlanId];
  const href = buildReportInputHref({
    sourcePath: source,
    intent: resolvedIntent,
    planId: resolvedPlanId,
    language,
    birthContext,
  });
  const presentationId = offerPresentationId({
    actionMode: "input_collection",
    planId: resolvedPlanId,
    placement,
    sourcePath: source,
  });

  return (
    <section className="mt-6 rounded-lg border border-[var(--ish-border)] bg-[var(--ish-bg-card)] p-5 text-center shadow-sm">
      {/* Render-nothing telemetry island: makes this priced report CTA's reach
          measurable (offer_seen), fired once. Without it muhurat/dasha/sade-sati
          read 0% reach despite the CTA being on screen (ADR-0025, rule 46). */}
      <OfferImpression
        planId={resolvedPlanId}
        actionMode="input_collection"
        sourcePath={source}
        intent={resolvedIntent}
        placement={placement}
        presentationKey={presentationId}
      />
      <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-intent-brand">
        {language === "hi" ? "अगला चरण" : "Next Step"}
      </p>
      <h2 className="mt-2 font-serif text-[22px] leading-tight text-[var(--ish-dark)]">{title}</h2>
      <p className="mx-auto mt-2 max-w-xl text-body-small leading-6 text-[var(--ish-text-muted)]">
        {body}
      </p>
      {/* Masked teaser of the paid report's depth above the unlock CTA. This
          neutral locked-depth pattern is presentational: DepthPaywall
          fires NO impression (the surface keeps its single OfferImpression above),
          renders the single CTA as its child (rule 39), and degrades to just the
          CTA when this planId has no preview configured (rules 46/47/58). */}
      <DepthPaywall planId={resolvedPlanId} language={language}>
        <BridgeOfferLink
          href={href}
          planId={resolvedPlanId}
          sourcePath={source}
          intent={resolvedIntent}
          placement={placement}
          presentationId={presentationId}
          className="mt-4 inline-flex min-h-11 items-center justify-center rounded-md bg-[var(--ish-accent)] px-5 text-body font-semibold text-white"
        >
          {cta} · ₹{report.price_inr.toLocaleString("en-IN")}
        </BridgeOfferLink>
      </DepthPaywall>
    </section>
  );
}
